// API client. All figures come from the server; the browser only renders and sorts for display.
export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly code?: string, public readonly details?: unknown) { super(message); }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.ok) {
    const ct = res.headers.get('content-type') || '';
    return (ct.includes('application/json') ? res.json() : res.text()) as Promise<T>;
  }
  let body: { error?: string; code?: string; details?: unknown } = {};
  try { body = await res.json(); } catch { /* not JSON */ }
  if (res.status === 401) window.dispatchEvent(new CustomEvent('ms:unauthenticated'));
  throw new ApiError(res.status, body.error || res.statusText || 'Request failed', body.code, body.details);
}

const H = { 'X-Requested-With': 'ms-app' };

export const api = {
  get: <T>(url: string) => fetch(url, { headers: H, credentials: 'same-origin' }).then(r => handle<T>(r)),
  post: <T>(url: string, body?: unknown) => fetch(url, { method: 'POST', headers: { ...H, 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body ?? {}) }).then(r => handle<T>(r)),
  patch: <T>(url: string, body?: unknown) => fetch(url, { method: 'PATCH', headers: { ...H, 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body ?? {}) }).then(r => handle<T>(r)),
  upload: <T>(url: string, form: FormData) => fetch(url, { method: 'POST', headers: H, credentials: 'same-origin', body: form }).then(r => handle<T>(r)),
  download: async (url: string, body: unknown, filename: string) => {
    const r = await fetch(url, { method: 'POST', headers: { ...H, 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) });
    if (!r.ok) await handle(r);
    const blob = await r.blob();
    const u = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 30_000);   // revoking in the same tick can cancel the download
  }
};

export const qs = (o: Record<string, string | null | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? '?' + s : '';
};

export const errText = (e: unknown) => {
  if (e instanceof ApiError) {
    const d = Array.isArray(e.details) ? e.details.filter(x => typeof x === 'string') : [];
    return e.message + (d.length ? ' ' + d.join(' ') : '');
  }
  return (e as Error)?.message || String(e);
};
